import React, {
  MouseEvent,
  TouchEvent,
  useContext,
  useEffect,
  useState,
} from 'react';
import Block from '@legacy-vfuk/core-block';
import CardCarousel from '@legacy-vfuk/core-card-carousel';
import RichTextElement from '../../Elements/RichText';
import {
  SectionTestimonialRibbonProps,
  SectionTestimonialRibbonRowProps,
  CustomWindow,
} from './types/SectionTestimonialRibbonRow.types';
import { PhotoAvatar } from '@legacy-vfuk/core-photo-avatar';
import { Heading } from '@legacy-vfuk/core-heading';
import { Paragraph } from '@legacy-vfuk/core-paragraph';
import RichText from '../../Elements/RichText';

import {
  BlockWrapper,
  whiteCSS,
  galleryCSS,
  ContainerWrapper,
  ContentWrapper,
  CaroulselWrapper,
  FeedBackWrapper,
  TestimonialContentWrapper,
  TestimonialContainerWrapper,
} from './styles/SectionTestimonialRibbonRow.styles';
import { DirectionContext } from '../../../../contexts/DirectionContext';
import { getImageSrcFromGatsbyImageData } from '../../../../helpers/gatsbyImageHelper';
const MOBILE_VIEW_WIDTH = 794;

declare const window: CustomWindow;
const Testimonial: React.FC<SectionTestimonialRibbonProps> = ({
  avatarImage,
  feedback,
  personJobTitle,
  personName,
  backgroundColor,
}: SectionTestimonialRibbonProps) => {
  const testimonialAppearance =
    backgroundColor === 'white' ? galleryCSS : whiteCSS;
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [startX, setStartX] = useState<number>(0);

  const handleMouseDown = (
    e: MouseEvent<HTMLDivElement> | TouchEvent<HTMLDivElement>
  ) => {
    setIsDragging(false);
    const clientX = 'clientX' in e ? e.clientX : e.touches[0].clientX;
    setStartX(clientX);
  };

  const handleMouseMove = (
    e: MouseEvent<HTMLDivElement> | TouchEvent<HTMLDivElement>
  ) => {
    if (startX === 0) return;

    const currentX = 'clientX' in e ? e.clientX : e.touches[0].clientX;
    const distance = Math.abs(currentX - startX);

    if (distance > 10) {
      setIsDragging(true);
    }
  };

  const handleMouseUp = (
    e: MouseEvent<HTMLDivElement> | TouchEvent<HTMLDivElement>
  ) => {
    if (isDragging) {
      const endX = 'clientX' in e ? e.clientX : e.changedTouches[0].clientX;
      const direction = endX < startX ? 'left' : 'right';
      window.onCarouselArrowClickEvent(direction);
    }

    setStartX(0);
    setIsDragging(false);
  };

  return (
    <TestimonialContainerWrapper
      css={testimonialAppearance}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onTouchStart={handleMouseDown}
      onTouchMove={handleMouseMove}
      onTouchEnd={handleMouseUp}
    >
      <TestimonialContentWrapper>
        {avatarImage?.gatsbyImageData && (
          <div>
            <PhotoAvatar
              image={getImageSrcFromGatsbyImageData(
                avatarImage.gatsbyImageData
              )}
            />
          </div>
        )}

        <Heading level={4} weight={3}>
          <FeedBackWrapper>
            <RichText rawRichText={feedback?.raw} />
          </FeedBackWrapper>
        </Heading>

        <Paragraph size={2} weight={3} noMargin>
          {personName}
        </Paragraph>

        <Paragraph size={1} weight={2}>
          {personJobTitle}
        </Paragraph>
      </TestimonialContentWrapper>
    </TestimonialContainerWrapper>
  );
};

const useCheckWindowSize = () => {
  const [isMobile, setIsMobile] = useState(false);
  if (typeof window !== 'undefined') {
    useEffect(() => {
      function handleResize() {
        if (window.innerWidth <= MOBILE_VIEW_WIDTH) {
          setIsMobile(true);
        }
      }
      window.addEventListener('resize', handleResize);
      handleResize();
      return () => window.removeEventListener('resize', handleResize);
    }, []);
  }
  return isMobile;
};

const SectionTestimonialRibbonRow = ({
  content,
  backgroundColor,
  testimonialAlignment,
  testimonials,
}: SectionTestimonialRibbonRowProps) => {
  const direction = useContext(DirectionContext);
  const backgroundCss = backgroundColor === 'white' ? whiteCSS : galleryCSS;
  const isMediaLeft = testimonialAlignment === 'left' || direction === 'rtl';

  const isMobile = useCheckWindowSize();

  const theContent = content?.map(item => {
    return (
      <RichTextElement
        key={item.id}
        rawRichText={item.text.raw}
        color={item.color}
        textAlign={item.textAlign}
      />
    );
  });

  return (
    <BlockWrapper>
      <Block css={backgroundCss}>
        <ContainerWrapper
          isMediaLeft={isMediaLeft}
          data-testid="section-testimonial-ribbon-row-container"
        >
          <ContentWrapper isMediaLeft={isMediaLeft}>
            {theContent}
          </ContentWrapper>
          <CaroulselWrapper>
            <CardCarousel
              id="section-testimonial-ribbon-row"
              srName="carousel-testimonial"
              slidesToShow={{ xl: 2, lg: 2, md: 2, sm: 1 }}
              slidesToScroll={{ xl: 1, lg: 1, md: 1, sm: 1 }}
              allowOverflow={isMobile ? false : true}
            >
              {testimonials.map((testimonial, index) => (
                <Testimonial
                  key={`testimonial-${index}`}
                  {...testimonial}
                  backgroundColor={backgroundColor}
                />
              ))}
            </CardCarousel>
          </CaroulselWrapper>
        </ContainerWrapper>
      </Block>
    </BlockWrapper>
  );
};

export default SectionTestimonialRibbonRow;
